import { useState, useEffect } from 'react';

export default function Footer() {
  const [footerContent, setFooterContent] = useState({
    footer_title1: 'Let\'s Work Together',
    footer_title1_font: 'Archivo',
    footer_description1: 'Ready to bring your creative vision to life? We\'d love to hear about your project.',
    footer_title2: 'Connect',
    footer_title2_font: 'Archivo',
    footer_email: 'hello@creativeduo.com',
    footer_phone1: '+1 (555) 123-4567',
    footer_phone2: '+1 (555) 987-6543',
    footer_instagram1: '#',
    footer_instagram2: '#',
    footer_linkedin1: '#',
    footer_linkedin2: '#',
    footer_linkedin1_name: '',
    footer_linkedin2_name: '',
    footer_behance: '#',
    footer_title3: 'Studio',
    footer_title3_font: 'Archivo',
    footer_studio_info: 'Creative Duo Studio<br />Art Direction & Copywriting<br />18+ Years of Excellence',
    footer_copyright: '© 2024 Creative Duo. All rights reserved.'
  });

  // Helper function to render text with line breaks
  const renderTextWithBreaks = (text: string) => {
    return text.split('<br>').map((line, index, array) => (
      <span key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </span>
    ));
  };

  // Fetch footer content from admin
  useEffect(() => {
    fetch('/admin/content-api.php')
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data) {
          setFooterContent(prevContent => ({
            ...prevContent,
            ...data.data
          }));
        }
      })
      .catch(error => {
        console.log('Could not fetch footer content:', error);
      });
  }, []);

  return (
    <footer className="bg-gray-800 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="md:col-span-2">
            <h3 
              className="text-lg font-semibold mb-4"
              style={{ fontFamily: `'${footerContent.footer_title1_font}', sans-serif` }}
            >
              {footerContent.footer_title1}
            </h3>
            {footerContent.footer_description1 && (
              <p className="text-gray-300 leading-relaxed">
                {renderTextWithBreaks(footerContent.footer_description1)}
              </p>
            )}
          </div>
          
          <div>
            <h3 
              className="text-lg font-semibold mb-4"
              style={{ fontFamily: `'${footerContent.footer_title2_font}', sans-serif` }}
            >
              {footerContent.footer_title2}
            </h3>
            <div className="space-y-3">
              <a 
                href={`mailto:${footerContent.footer_email}`} 
                className="block text-gray-600 hover:text-black transition-colors cursor-pointer text-sm"
              >
                <i className="ri-mail-line mr-2"></i>
                {footerContent.footer_email}
              </a>
              
              <div className="flex flex-wrap gap-3 mt-4">
                {footerContent.footer_instagram1 && footerContent.footer_instagram1 !== '#' && (
                  <a 
                    href={footerContent.footer_instagram1} 
                    className="text-gray-600 hover:text-black transition-colors cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Instagram 1"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                )}
                
                {footerContent.footer_instagram2 && footerContent.footer_instagram2 !== '#' && (
                  <a 
                    href={footerContent.footer_instagram2} 
                    className="text-gray-600 hover:text-black transition-colors cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Instagram 2"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                )}
                
                {footerContent.footer_linkedin1 && footerContent.footer_linkedin1 !== '#' && (
                  <div className="flex items-center space-x-2">
                    <a 
                      href={footerContent.footer_linkedin1} 
                      className="text-gray-600 hover:text-black transition-colors cursor-pointer"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="LinkedIn 1"
                    >
                      <i className="ri-linkedin-line text-lg"></i>
                    </a>
                    {footerContent.footer_linkedin1_name && (
                      <span className="text-gray-600 text-sm">{footerContent.footer_linkedin1_name}</span>
                    )}
                  </div>
                )}
                
                {footerContent.footer_linkedin2 && footerContent.footer_linkedin2 !== '#' && (
                  <div className="flex items-center space-x-2">
                    <a 
                      href={footerContent.footer_linkedin2} 
                      className="text-gray-600 hover:text-black transition-colors cursor-pointer"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="LinkedIn 2"
                    >
                      <i className="ri-linkedin-line text-lg"></i>
                    </a>
                    {footerContent.footer_linkedin2_name && (
                      <span className="text-gray-600 text-sm">{footerContent.footer_linkedin2_name}</span>
                    )}
                  </div>
                )}
                
                {footerContent.footer_behance && footerContent.footer_behance !== '#' && (
                  <a 
                    href={footerContent.footer_behance} 
                    className="text-gray-600 hover:text-black transition-colors cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Behance"
                  >
                    <i className="ri-behance-line text-lg"></i>
                  </a>
                )}
              </div>
              
              {/* Phone numbers underneath social links */}
              <div className="space-y-2 mt-4">
                {footerContent.footer_phone1 && footerContent.footer_phone1 !== '+1 (555) 123-4567' && (
                  <a 
                    href={`tel:${footerContent.footer_phone1}`} 
                    className="block text-gray-600 hover:text-black transition-colors cursor-pointer text-sm"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    {footerContent.footer_phone1}
                  </a>
                )}
                
                {footerContent.footer_phone2 && footerContent.footer_phone2 !== '+1 (555) 987-6543' && (
                  <a 
                    href={`tel:${footerContent.footer_phone2}`} 
                    className="block text-gray-600 hover:text-black transition-colors cursor-pointer text-sm"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    {footerContent.footer_phone2}
                  </a>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <h3 
              className="text-lg font-semibold mb-4"
              style={{ fontFamily: `'${footerContent.footer_title3_font}', sans-serif` }}
            >
              {footerContent.footer_title3}
            </h3>
            <p 
              className="text-gray-600 text-sm"
              dangerouslySetInnerHTML={{ __html: footerContent.footer_studio_info.replace(/\n/g, '<br />') }}
            />
          </div>
        </div>
        
        <div className="border-t border-gray-100 mt-12 pt-8">
          <p className="text-gray-500 text-sm">
            {footerContent.footer_copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
